import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, Car, Wallet, Calendar, TrendingUp, AlertTriangle, CheckCircle, PoundSterling } from 'lucide-react';

const WhatIsPCPCarFinancePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                What is PCP Car Finance?
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Understanding Personal Contract Purchase finance and how it works
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                Personal Contract Purchase (PCP) is one of the most popular ways to finance a car in the 
                UK. It allows you to drive a new or nearly-new vehicle with relatively low monthly payments, 
                with options at the end of the agreement to either <strong>return the car, purchase it outright, 
                or use any equity towards a new vehicle</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* How Does PCP Work Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Car className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  How Does PCP Work?
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                A PCP agreement typically works in three stages:
              </p>

              <div className="space-y-6">
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0 text-white font-bold">
                        1
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-2">Deposit</h3>
                        <p className="text-muted-foreground">
                          You pay an initial deposit, which is usually around <strong className="text-foreground">10% of 
                          the vehicle's value</strong>, though this can vary.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center flex-shrink-0 text-primary font-bold">
                        2
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-2">Monthly Payments</h3>
                        <p className="text-muted-foreground">
                          You make fixed monthly payments over an agreed term, typically <strong className="text-foreground">24 to 48 months</strong>.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0 text-white font-bold">
                        3
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-2">Balloon Payment</h3>
                        <p className="text-muted-foreground">
                          At the end of the agreement, there is a large final payment (often called the "balloon" 
                          or "optional final payment") representing the vehicle's predicted future value.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* End of PCP Options */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <Calendar className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  Your Options at the End of PCP
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-8">
                At the end of a PCP agreement, you typically have three choices:
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">Return the Car</h3>
                  <p className="text-primary-foreground/80">
                    Hand the vehicle back to the finance company and walk away with nothing further to pay 
                    (assuming you have stayed within mileage limits and the car is in good condition).
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">Pay the Balloon</h3>
                  <p className="text-primary-foreground/80">
                    Make the final balloon payment and become the <strong className="text-gold">outright owner</strong> of the vehicle.
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3">Part-Exchange</h3>
                  <p className="text-primary-foreground/80">
                    If the car is worth more than the balloon payment (you have "equity"), you can use this 
                    towards the <strong className="text-gold">deposit on a new PCP agreement</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                Key Features of PCP
              </h2>

              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-gold" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">Guaranteed Minimum Future Value (GMFV)</h3>
                  </div>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        The balloon payment is based on the Guaranteed Minimum Future Value – the finance 
                        company's prediction of what the car will be worth at the end of the agreement. 
                        <strong className="text-foreground"> This protects you if the car's actual value falls below this amount.</strong>
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Car className="w-5 h-5 text-accent" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">Mileage Restrictions</h3>
                  </div>
                  <Card className="border-l-4 border-l-accent">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        PCP agreements include an annual mileage limit, typically between <strong className="text-foreground">8,000 and 15,000 miles 
                        per year</strong>. If you exceed this limit, you will be charged for each additional mile when you 
                        return the vehicle.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-gold" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">Condition Requirements</h3>
                  </div>
                  <Card className="border-l-4 border-l-gold">
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        The vehicle must be returned in good condition, within the fair wear and tear guidelines. 
                        <strong className="text-foreground"> Excessive damage may result in additional charges.</strong>
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PCP vs HP Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                PCP vs HP (Hire Purchase)
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                While PCP and HP are both forms of car finance, they work differently:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-accent bg-accent/5">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-4 text-xl">HP (Hire Purchase)</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span>Pay off the <strong className="text-foreground">full value</strong> of the car</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span>Higher monthly payments than PCP</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span><strong className="text-foreground">Own the car outright</strong> at the end</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-gold bg-gold/5">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-4 text-xl">PCP (Personal Contract Purchase)</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                        <span>Only pay off the <strong className="text-foreground">depreciation</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                        <span>Lower monthly payments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                        <span>Don't own unless you pay balloon</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Commission Section */}
        <section className="py-16 lg:py-20 bg-destructive/5">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Commission Problem
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                When you take out PCP finance through a car dealer, the dealer typically receives 
                commission from the lender. Before January 2021, some arrangements allowed dealers to 
                <strong className="text-foreground"> increase the interest rate you paid in order to earn more commission – without telling you</strong>.
              </p>

              <Card className="border-destructive bg-destructive/5 mb-8">
                <CardContent className="p-6">
                  <p className="text-foreground font-medium">
                    The FCA banned these Discretionary Commission Arrangements (DCAs) in 2021 and is now 
                    investigating how consumers may have been affected. <strong>If you took out PCP finance before 
                    this ban and were not told about commission, you may be entitled to compensation.</strong>
                  </p>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-accent mb-2">£830</div>
                    <p className="text-sm text-muted-foreground">
                      Estimated average compensation per affected agreement
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-gold mb-2">£7.5bn</div>
                    <p className="text-sm text-muted-foreground">
                      Potential total redress to lenders according to FCA estimates
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Is PCP Right Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                Is PCP Right for You?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                PCP can be a suitable option if:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "You want lower monthly payments than HP",
                  "You like changing your car every few years",
                  "You don't drive excessive miles",
                  "You take good care of your vehicles"
                ].map((item, idx) => (
                  <Card key={idx} className="border-border">
                    <CardContent className="p-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <p className="text-foreground">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gold/5 border-gold">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    However, it's important to understand the terms of your agreement and ensure you are 
                    getting a <strong>fair deal on the interest rate</strong>. If you weren't told about commission, 
                    you may not have received the best rate available to you.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Think Your PCP Finance Was Mis-Sold?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Blue Lion Law can assess your individual circumstances and advise whether you may be entitled to compensation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                    Find Your Agreements
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="tel:02081530033">
                    Call 0208 153 0033
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/pcp/how-pcp-is-mis-sold" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How PCP is Mis-sold</span>
                </a>
                <a href="/pcp/fca-motor-finance-compensation-scheme" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">FCA Motor Finance Scheme</span>
                </a>
                <a href="/pcp-car-finance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">PCP Car Finance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WhatIsPCPCarFinancePage;
